import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FormikErrors, FormikHelpers, useFormik } from 'formik'

import { Loading } from '../../../global/Loading/Loading'
import { NavigationArrowDownIcon } from '../../../icons/NavigationArrowDownIcon'
import { IntroducingBrokerRebateGroupDto } from '../../../model/IntroducingBrokerRebateGroupDto'
import { NameDto } from '../../../model/NameDto'
import { Text, TextStrong } from '../../../ui/Typography/Typography'
import { DropdownInput } from '../../../ui/v2/DropdownInput/DropdownInput'
import { Input } from '../../../ui/v2/Input/Input'
import { ModalContentWrapper } from '../../../ui/v2/Modal/ModalContentWrapper'
import { useWindowResize } from '../../../utils/domUtils'
import { IAppendableFetchResult } from '../../../utils/useFetch'

import styles from './AddToGroupModal.module.scss'

interface AddToGroupModalProps {
  closeModal: () => void
  onSubmit: (values: Partial<AddToGroupFormValues>) => Promise<void>
  openGroupSelectionModal: () => void
  initialValues?: Partial<AddToGroupFormValues>
  groupOptions?: NameDto<string>[]
  groupsQuery: IAppendableFetchResult<IntroducingBrokerRebateGroupDto[]>
}

export const AddToGroupModal: FC<AddToGroupModalProps> = (props) => {
  const {
    closeModal,
    onSubmit,
    initialValues,
    groupOptions,
    groupsQuery,
    openGroupSelectionModal,
  } = props

  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const { setFieldValue, errors, values, handleSubmit, setErrors, resetForm, isSubmitting } =
    useAddToGroupForm({
      initialValues: initialValues || DEFAULT_VALUES,
      handleSubmit: async (values, { resetForm, setSubmitting }) => {
        setSubmitting(true)
        await onSubmit(values)
        setSubmitting(false)
        resetForm()
      },
    })

  const hasErrors = useMemo(() => Object.values(errors).some((error) => !!error), [errors])

  const isApplyDisabled = hasErrors || isSubmitting || !values.groupId

  const selectedGroup = useMemo(
    () => groupsQuery.data.find((group) => group.id === values.groupId),
    [groupsQuery, values.groupId]
  )

  const handleChange = async (key: string, value: any) => {
    setFieldValue(key, value)

    if (hasErrors) {
      setErrors({
        ...errors,
        [key]: undefined,
      })
    }
  }

  const handleClose = () => {
    setErrors({})
    resetForm()
    closeModal()
  }

  return (
    <ModalContentWrapper
      title={t('IB.RebatesPage.ClientsPage.Adding to group')}
      applyLabel={t('Apply')}
      isApplyDisabled={isApplyDisabled}
      onClose={handleClose}
      onConfirm={handleSubmit}
      onCancel={handleClose}
      isCancelShownOnDesktop
    >
      <Loading showLoadingIcon isLoading={groupsQuery.isLoading}>
        <div className={styles.addToGroupModal}>
          {isMobile ? (
            <Input
              value={groupOptions?.find((type) => type.id === values.groupId)?.name}
              placeholder={t('IB.RebatesPage.ClientsPage.Available group')}
              onClick={openGroupSelectionModal}
              hint={errors.groupId}
              error={!!errors.groupId}
              endIcon={NavigationArrowDownIcon}
              readOnly
            />
          ) : (
            <DropdownInput<NameDto<string>>
              value={values.groupId}
              onChange={(option) => handleChange('groupId', option.id)}
              options={groupOptions}
              placeholder={t('IB.RebatesPage.ClientsPage.Available group')}
              hint={errors.groupId}
              error={!!errors.groupId}
              isScrollable
            />
          )}
          {selectedGroup && (
            <>
              <div className={styles.groupInfoItem}>
                <Text>{t('Type')}</Text>
                <TextStrong>{selectedGroup?.rebateApprovalType.name}</TextStrong>
              </div>
              <div className={styles.groupInfoItem}>
                <Text>{t('IB.RebatesPage.Payout period')}</Text>
                <TextStrong>{selectedGroup?.rebatePeriod.name}</TextStrong>
              </div>
              <div className={styles.groupInfoItem}>
                <Text>{t('IB.RebatesPage.ClientsPage.Commission')}</Text>
                <TextStrong>{selectedGroup?.rebateAmountInPercentage}%</TextStrong>
              </div>
            </>
          )}
        </div>
      </Loading>
    </ModalContentWrapper>
  )
}

export interface AddToGroupFormValues {
  groupId: string
}

interface AddToGroupFormParams {
  handleSubmit: (
    values: Partial<AddToGroupFormValues>,
    formikHelpers: FormikHelpers<Partial<AddToGroupFormValues>>
  ) => void
  initialValues?: Partial<AddToGroupFormValues>
}

const DEFAULT_VALUES: Partial<AddToGroupFormValues> = {
  groupId: undefined,
}

export const useAddToGroupForm = ({
  handleSubmit,
  initialValues = DEFAULT_VALUES,
}: AddToGroupFormParams): ReturnType<typeof useFormik<Partial<AddToGroupFormValues>>> => {
  const { t } = useTranslation()

  return useFormik<Partial<AddToGroupFormValues>>({
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validate: (values) => {
      const errors: FormikErrors<AddToGroupFormValues> = {}

      if (!values.groupId) {
        errors.groupId = t('Validation.Required')
      }

      return errors
    },
    validateOnChange: false,
  })
}
